import { create } from 'zustand';
import { Workspace, TabState as TabStateData } from '../types';
import { apiService } from '../services/api';
import { NAMES } from '../data/names';

export interface Tab {
  id: string;
  type: 'email' | 'workspace' | 'settings';
  title: string;
  workspace?: Workspace;
  savedStateId?: string;
  isActive: boolean;
  state?: TabStateData;
  isDirty?: boolean; // Track if tab has unsaved changes
  isSaving?: boolean; // Track if tab is currently being saved
}

export interface TabState {
  tabs: Tab[];
  activeTabId: string | null;
  isInitialized: boolean;
  
  // Actions
  addTab: (tab: Omit<Tab, 'id' | 'isActive'>) => string;
  closeTab: (tabId: string) => void;
  requestCloseTab: (tabId: string, onSaveConfirmed?: (name: string) => void, onCancelClose?: () => void) => { needsSave: boolean; tab?: Tab };
  forceCloseTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  updateTabState: (tabId: string, state: TabStateData) => void;
  markTabDirty: (tabId: string) => void;
  markTabClean: (tabId: string) => void;
  saveTabState: (tabId: string, name: string, autoOpenOnStartup?: boolean) => Promise<void>;
  restoreTabState: (workspace: Workspace, savedStateId: string) => Promise<string>;
  initializeDefaultTabs: () => void;
  autoRestoreStates: () => Promise<void>;
}

export const useTabStore = create<TabState>((set, get) => ({
  tabs: [],
  activeTabId: null,
  isInitialized: false,

  addTab: (tabData) => {
    const id = `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    
    set((state) => {
      // Deactivate all existing tabs
      const updatedTabs = state.tabs.map(tab => ({ ...tab, isActive: false }));
      
      // Add new active tab
      const newTab: Tab = {
        ...tabData,
        id,
        isActive: true,
        isDirty: false, // New tabs start clean
        isSaving: false, // New tabs not saving
      };
      
      return {
        tabs: [...updatedTabs, newTab],
        activeTabId: id,
      };
    });
    
    return id;
  },

  requestCloseTab: (tabId, onSaveConfirmed, onCancelClose) => {
    const state = get();
    const tab = state.tabs.find(t => t.id === tabId);
    
    if (!tab) {
      return { needsSave: false };
    }

    // Check if tab needs saving (workspace tab that's dirty and hasn't been saved)
    const needsSave = tab.type === 'workspace' && 
                      tab.workspace && 
                      tab.isDirty === true;

    if (needsSave) {
      return { needsSave: true, tab };
    } else {
      // Tab doesn't need saving, close immediately
      get().forceCloseTab(tabId);
      return { needsSave: false };
    }
  },

  forceCloseTab: (tabId) => {
    set((state) => {
      const tabIndex = state.tabs.findIndex(tab => tab.id === tabId);
      if (tabIndex === -1) return state;

      const newTabs = state.tabs.filter(t => t.id !== tabId);
      
      // If closing active tab, activate another tab
      let newActiveTabId = state.activeTabId;
      if (state.activeTabId === tabId) {
        if (newTabs.length > 0) {
          // Activate the tab to the left, or the first tab if closing the first tab
          const newActiveIndex = Math.max(0, tabIndex - 1);
          newActiveTabId = newTabs[newActiveIndex]?.id || null;
          if (newActiveTabId) {
            newTabs[newActiveIndex].isActive = true;
          }
        } else {
          newActiveTabId = null;
        }
      }

      return {
        tabs: newTabs,
        activeTabId: newActiveTabId,
      };
    });
  },

  closeTab: (tabId) => {
    set((state) => {
      const tabIndex = state.tabs.findIndex(tab => tab.id === tabId);
      if (tabIndex === -1) return state;

      // Note: Auto-save logic removed - user must explicitly save workspace states

      const newTabs = state.tabs.filter(t => t.id !== tabId);
      
      // If closing active tab, activate another tab
      let newActiveTabId = state.activeTabId;
      if (state.activeTabId === tabId) {
        if (newTabs.length > 0) {
          // Activate the tab to the left, or the first tab if closing the first tab
          const newActiveIndex = Math.max(0, tabIndex - 1);
          newActiveTabId = newTabs[newActiveIndex]?.id || null;
          if (newActiveTabId) {
            newTabs[newActiveIndex].isActive = true;
          }
        } else {
          newActiveTabId = null;
        }
      }

      return {
        tabs: newTabs,
        activeTabId: newActiveTabId,
      };
    });
  },

  setActiveTab: (tabId) => {
    set((state) => ({
      tabs: state.tabs.map(tab => ({
        ...tab,
        isActive: tab.id === tabId,
      })),
      activeTabId: tabId,
    }));
  },

  updateTabState: (tabId, newState) => {
    set((state) => {
      const tabIndex = state.tabs.findIndex(tab => tab.id === tabId);
      if (tabIndex === -1) return state; // Tab not found, no update
      
      const updatedTabs = [...state.tabs];
      const currentTab = updatedTabs[tabIndex];
      
      // Prevent updates to tabs that are currently saving
      if (currentTab.isSaving) {
        return state;
      }
      
      updatedTabs[tabIndex] = {
        ...currentTab,
        state: { ...currentTab.state, ...newState }
      };
      
      return {
        tabs: updatedTabs,
      };
    });
  },

  markTabDirty: (tabId) => {
    set((state) => ({
      tabs: state.tabs.map(tab =>
        tab.id === tabId
          ? { ...tab, isDirty: true }
          : tab
      ),
    }));
  },

  markTabClean: (tabId) => {
    set((state) => ({
      tabs: state.tabs.map(tab =>
        tab.id === tabId
          ? { ...tab, isDirty: false }
          : tab
      ),
    }));
  },

  saveTabState: async (tabId, name, autoOpenOnStartup) => {
    // Atomic state snapshot to prevent race conditions
    const state = get();
    const tab = state.tabs.find(t => t.id === tabId);
    if (!tab || tab.type !== 'workspace' || !tab.workspace) {
      return;
    }

    // Mark tab as saving to prevent concurrent saves
    set((currentState) => ({
      tabs: currentState.tabs.map(t =>
        t.id === tabId
          ? { ...t, isSaving: true }
          : t
      ),
    }));

    try {
      // Capture current state data atomically
      const currentTab = get().tabs.find(t => t.id === tabId);
      if (!currentTab?.workspace) {
        throw new Error('Tab or workspace no longer exists');
      }

      // If this tab is already linked to a saved state, update it instead of creating a new one
      if (currentTab.savedStateId) {
        // Filter out truly empty messages, but keep assistant messages even with empty content
        // Also strip out large binary content to avoid validation errors
        const filteredMessages = (currentTab.state?.messages?.filter(msg => {
          // Always keep user, system, and tool messages if they have any content
          if (msg.role !== 'assistant') {
            return msg.content && msg.content.trim().length > 0;
          }
          // For assistant messages, only keep them if they have content
          return msg.content && msg.content.trim().length > 0;
        }) || []).map(msg => {
          // Strip out large binary content from messages to avoid exceeding size limits
          let cleanContent = msg.content;
          
          // Check if message contains binary file content (base64 encoded)
          if (cleanContent && cleanContent.includes('[Binary file:') && cleanContent.includes('Base64 content:')) {
            // Replace the base64 content with a placeholder
            cleanContent = cleanContent.replace(/Base64 content: [A-Za-z0-9+/=]+/g, 'Base64 content: [Content removed for storage]');
          }
          
          // Also handle very long messages by truncating them
          if (cleanContent && cleanContent.length > 64000) { // Leave some buffer under the 64K limit
            cleanContent = cleanContent.substring(0, 64000) + '... [Content truncated for storage]';
          }
          
          const formattedMsg = {
            ...msg,
            content: cleanContent,
            id: msg.id || crypto.randomUUID(), // Ensure message has an ID
            timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp // Ensure timestamp is ISO string
          };
          return formattedMsg;
        });
        
        const updatedState = await apiService.updateSavedState(currentTab.savedStateId, {
          name, // Use the provided name
          stateData: currentTab.state || {},
          sessionId: currentTab.state?.sessionId,
          chatMessages: filteredMessages,
          chatSettings: { ...currentTab.state?.settings, aiName: currentTab.workspace?.aiName },
          autoOpenOnStartup,
        });
        
        // Dispatch custom event to notify navigation tree
        window.dispatchEvent(new CustomEvent('workspaceSavedStatesChanged'));
        
        // Mark tab as clean (only if tab still exists)
        set((currentState) => {
          const tabIndex = currentState.tabs.findIndex(t => t.id === tabId);
          if (tabIndex === -1) return currentState; // Tab was closed during save
          
          return {
            tabs: currentState.tabs.map(t =>
              t.id === tabId
                ? { ...t, isDirty: false, isSaving: false }
                : t
            ),
          };
        });
        
        return; // Exit early for update case
      }
      
      // For new saved states, generate a unique name with timestamp
      const timestamp = new Date().toLocaleString().replace(/[/:,]/g, '-');
      const rawName = `${name} - ${timestamp}`;
      
      // Sanitize the name to match the validation pattern /^[a-zA-Z0-9\s._-]+$/
      const finalName = rawName
        .replace(/[^a-zA-Z0-9\s._-]/g, '_') // Replace invalid characters with underscores
        .replace(/_{2,}/g, '_') // Replace multiple consecutive underscores with single underscore
        .trim();

      // Filter out truly empty messages
      // Also strip out large binary content to avoid validation errors
      const filteredMessages = (currentTab.state?.messages?.filter(msg => {
        // Always keep user, system, and tool messages if they have any content
        if (msg.role !== 'assistant') {
          return msg.content && msg.content.trim().length > 0;
        }
        // For assistant messages, only keep them if they have content
        return msg.content && msg.content.trim().length > 0;
      }) || []).map(msg => {
        // Strip out large binary content from messages to avoid exceeding size limits
        let cleanContent = msg.content;
        
        // Check if message contains binary file content (base64 encoded)
        if (cleanContent && cleanContent.includes('[Binary file:') && cleanContent.includes('Base64 content:')) {
          // Replace the base64 content with a placeholder
          cleanContent = cleanContent.replace(/Base64 content: [A-Za-z0-9+/=]+/g, 'Base64 content: [Content removed for storage]');
        }
        
        // Also handle very long messages by truncating them
        if (cleanContent && cleanContent.length > 64000) { // Leave some buffer under the 64K limit
          cleanContent = cleanContent.substring(0, 64000) + '... [Content truncated for storage]';
        }
        
        const formattedMsg = {
          ...msg,
          content: cleanContent,
          id: msg.id || crypto.randomUUID(), // Ensure message has an ID
          timestamp: msg.timestamp instanceof Date ? msg.timestamp.toISOString() : msg.timestamp // Ensure timestamp is ISO string
        };
        return formattedMsg;
      });
      
      const savedState = await apiService.createSavedState({
        workspaceId: currentTab.workspace.id,
        name: finalName,
        stateData: currentTab.state || {},
        sessionId: currentTab.state?.sessionId,
        chatMessages: filteredMessages,
        chatSettings: { ...currentTab.state?.settings, aiName: currentTab.workspace?.aiName },
        autoOpenOnStartup,
      });
      
      // Dispatch custom event to notify navigation tree
      window.dispatchEvent(new CustomEvent('workspaceSavedStatesChanged'));
      
      // Mark tab as clean and update savedStateId (only if tab still exists)
      set((currentState) => {
        const tabIndex = currentState.tabs.findIndex(t => t.id === tabId);
        if (tabIndex === -1) return currentState; // Tab was closed during save
        
        return {
          tabs: currentState.tabs.map(t =>
            t.id === tabId
              ? { ...t, isDirty: false, savedStateId: savedState.id, isSaving: false }
              : t
          ),
        };
      });
    } catch (error) {
      console.error('Failed to save tab state:', error);
      
      // Remove saving flag on error
      set((currentState) => ({
        tabs: currentState.tabs.map(t =>
          t.id === tabId
            ? { ...t, isSaving: false }
            : t
        ),
      }));
      throw error;
    }
  },

  restoreTabState: async (workspace, savedStateId) => {
    try {
      // Check if a tab with this saved state is already open
      const existingTab = get().tabs.find(tab => 
        tab.savedStateId === savedStateId && 
        tab.workspace?.id === workspace.id
      );
      
      if (existingTab) {
        // Switch to existing tab instead of creating duplicate
        get().setActiveTab(existingTab.id);
        return existingTab.id;
      }
      
      const savedState = await apiService.getSavedState(savedStateId);
      
      // Merge saved state data with chat messages
      const restoredState = {
        ...savedState.stateData,
        messages: savedState.chatMessages || savedState.stateData.messages || [],
        settings: savedState.chatSettings || savedState.stateData.settings,
        sessionId: savedState.sessionId || savedState.stateData.sessionId
      };
      
      // Use the AI name from the saved state, or generate a random one as fallback
      const aiName = savedState.chatSettings?.aiName || 
                     restoredState.settings?.aiName || 
                     workspace.aiName || 
                     NAMES[Math.floor(Math.random() * NAMES.length)];
      
      const tabId = get().addTab({
        type: 'workspace',
        title: savedState.name, // Use just the saved state name for restored tabs
        workspace: {
          ...workspace,
          aiName // Update workspace object with random AI name
        },
        savedStateId,
        state: restoredState,
        isSaving: false,
      });

      return tabId;
    } catch (error) {
      console.error('Failed to restore tab state:', error);
      throw new Error('Saved state not found');
    }
  },

  initializeDefaultTabs: async () => {
    // Auto-restore states with auto-open flag set
    await get().autoRestoreStates();
    // Mark as initialized regardless of whether any tabs were restored
    set({ isInitialized: true });
  },

  autoRestoreStates: async () => {
    try {
      console.log('Starting optimized parallel auto-restore process');
      
      // Fetch saved states and workspaces in parallel for better performance
      const [autoOpenStates, workspaces] = await Promise.all([
        apiService.getSavedStates(undefined, true),
        apiService.getWorkspaces()
      ]);
      
      if (autoOpenStates.length === 0) {
        console.log('No auto-open states found');
        return;
      }

      console.log(`Processing ${autoOpenStates.length} auto-open states in parallel`);
      
      // Process state restorations in parallel instead of sequentially
      const restorePromises = autoOpenStates.map(async (savedState) => {
        const workspace = workspaces.find(w => w.id === savedState.workspaceId);
        if (workspace) {
          try {
            await get().restoreTabState(workspace, savedState.id);
            return { success: true, stateName: savedState.name };
          } catch (error) {
            console.error(`Failed to auto-restore state ${savedState.name}:`, error);
            return { success: false, stateName: savedState.name, error };
          }
        }
        return { success: false, stateName: savedState.name, error: 'Workspace not found' };
      });
      
      // Wait for all restorations to complete
      const results = await Promise.allSettled(restorePromises);
      
      const successful = results.filter(r => r.status === 'fulfilled' && r.value.success).length;
      console.log(`Parallel auto-restore completed: ${successful}/${autoOpenStates.length} states restored successfully`);
      
    } catch (error) {
      console.error('Failed to auto-restore states:', error);
    }
  },
}));